import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  Link,
  Checkbox,
  Pagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { Category } from '../types';
import { categoryService } from '../services/bookService';
import { useAppStore } from '../stores/useAppStore';
import { canManageCategories, isAdmin } from '../utils/rbac';
import { logger } from '../utils/logger';
import { useSnackbar } from '../hooks/useSnackbar';
import { ToastNotification } from './ToastNotification';
import { ExportDialog } from './ExportDialog';
import { type ExportColumn } from '../services/exportService';
import { isStudent } from '../utils/rbac';

interface CategoryListProps {
  onEdit: (category: Category) => void;
  onAdd: () => void;
  refreshTrigger?: number;
}

export const CategoryList: React.FC<CategoryListProps> = ({ onEdit, onAdd, refreshTrigger }) => {
  const navigate = useNavigate();
  const { user } = useAppStore();
  const userRoles = user?.roles || [];
  const canDelete = canManageCategories(userRoles);
  const isAdminRole = isAdmin(userRoles);

  // Debug logs
  logger.log('🔍 CategoryList - User:', user);
  logger.log('🔍 CategoryList - User roles:', userRoles);
  logger.log('🔍 CategoryList - canDelete:', canDelete);
  logger.log('🔍 CategoryList - isAdminRole:', isAdminRole);

  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();
  const canExport = !isStudent(userRoles);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load categories
  useEffect(() => {
    loadCategories();
  }, [debouncedSearchTerm, refreshTrigger, page]);

  // Reset page when search term changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await categoryService.getCategories({
        page,
        pageSize
      });

      let filtered = result.categories;

      // Apply client-side search filter if needed (for backward compatibility)
      if (debouncedSearchTerm.trim()) {
        const normalized = debouncedSearchTerm.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        filtered = result.categories.filter(cat => {
          const catName = cat.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          const catDesc = (cat.description || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          return catName.includes(normalized) || catDesc.includes(normalized);
        });
      }

      setCategories(filtered);
      setTotalCount(result.totalCount);
      setTotalPages(result.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (category: Category) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;

    try {
      const bookCount = categoryToDelete.bookCount || 0;

      // Check if category has books and user is not Admin
      if (bookCount > 0 && !isAdminRole) {
        showError(`Không thể xóa danh mục này vì có ${bookCount} cuốn sách đang sử dụng danh mục này. Vui lòng chuyển các sách sang danh mục khác trước khi xóa.`);
        setDeleteDialogOpen(false);
        setCategoryToDelete(null);
        return;
      }

      await categoryService.deleteCategory(categoryToDelete.id);
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
      showSuccess('Xóa danh mục thành công');
      await loadCategories();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete category';
      setError(errorMessage);
      showError(errorMessage);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      // Select all categories (no restriction)
      setSelectedCategoryIds(categories.map(cat => cat.id));
    } else {
      setSelectedCategoryIds([]);
    }
  };

  const handleSelectCategory = (categoryId: string, bookCount: number) => {
    logger.log('🔍 CategoryList - handleSelectCategory:', { categoryId, bookCount });

    setSelectedCategoryIds(prev => {
      const newIds = prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId];
      logger.log('🔍 CategoryList - Selected category IDs:', newIds);
      return newIds;
    });
  };

  const handleBulkDeleteClick = () => {
    if (selectedCategoryIds.length === 0) return;
    setBulkDeleteDialogOpen(true);
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      if (!isAdminRole) {
        // For non-Admin users, check if any category has books
        const categoriesWithBooks = selectedCategoryIds.filter(id => {
          const category = categories.find(cat => cat.id === id);
          return category && (category.bookCount || 0) > 0;
        });

        if (categoriesWithBooks.length > 0) {
          const categoryNames = categoriesWithBooks.map(id => {
            const cat = categories.find(c => c.id === id);
            return cat?.name || id;
          }).join(', ');

          showError(`Không thể xóa các danh mục: ${categoryNames}. Các danh mục này đang có sách. Vui lòng chuyển các sách sang danh mục khác trước khi xóa.`);
          setBulkDeleteDialogOpen(false);
          setSelectedCategoryIds([]);
          return;
        }
      }

      // Delete all selected categories (Admin can delete even with books, backend will handle)
      const deletePromises = selectedCategoryIds.map(id => categoryService.deleteCategory(id));
      await Promise.all(deletePromises);

      showSuccess(`Đã xóa ${selectedCategoryIds.length} danh mục thành công`);
      setBulkDeleteDialogOpen(false);
      setSelectedCategoryIds([]);
      await loadCategories();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Không thể xóa danh mục');
    }
  };

  const handleBulkDeleteCancel = () => {
    setBulkDeleteDialogOpen(false);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('vi-VN');
    } catch (error) {
      return '-';
    }
  };

  const handleExportCategories = async (dateFrom: Date | null, dateTo: Date | null): Promise<Category[]> => {
    // Categories don't need date range, but we keep the signature for consistency
    return await categoryService.exportCategories();
  };

  const categoryExportColumns: ExportColumn[] = [
    { key: 'name', label: 'Tên danh mục', width: 30 },
    { key: 'description', label: 'Mô tả', width: 40 },
    { key: 'bookCount', label: 'Số lượng sách', width: 15 },
    { key: 'dateCreated', label: 'Ngày tạo', width: 15 }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="baseline" gap={1}>
          <Typography variant="h4">QL danh mục</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            ({totalCount} danh mục)
          </Typography>
        </Box>
        <Tooltip title="Thêm danh mục mới" arrow>
          <IconButton
            color="primary"
            onClick={onAdd}
            aria-label="Thêm danh mục mới"
            size="small"
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            fullWidth
            placeholder="Tìm kiếm theo tên, mô tả..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          {canExport && (
            <Tooltip title="Xuất Excel" arrow>
              <IconButton
                color="primary"
                onClick={() => setExportDialogOpen(true)}
                aria-label="Xuất Excel"
                size="small"
              >
                <FileDownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Làm mới" arrow>
            <IconButton
              color="primary"
              onClick={() => {
                setSearchTerm('');
                loadCategories();
              }}
              aria-label="Làm mới"
              size="small"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canDelete && selectedCategoryIds.length > 0 && (
            <Tooltip title={`Xóa (${selectedCategoryIds.length})`} arrow>
              <IconButton
                color="error"
                onClick={handleBulkDeleteClick}
                aria-label="Xóa"
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Paper>

      <TableContainer component={Paper} sx={{ minHeight: 200, width: '100%' }}>
        <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '20%' }}>Tên danh mục</TableCell>
              <TableCell sx={{ width: '36%' }}>Mô tả</TableCell>
              <TableCell align="center" sx={{ width: '10%' }}>SL</TableCell>
              <TableCell sx={{ width: '12%' }}>Ngày tạo</TableCell>
              <TableCell align="center" sx={{ width: '15%' }}>Thao tác</TableCell>
              {canDelete && (
                <TableCell padding="none" align="center" sx={{ width: '14px', minWidth: '14px', maxWidth: '14px', padding: '0px !important' }}>
                  <Checkbox
                    indeterminate={
                      selectedCategoryIds.length > 0 &&
                      selectedCategoryIds.length < categories.length
                    }
                    checked={
                      categories.length > 0 &&
                      selectedCategoryIds.length === categories.length
                    }
                    onChange={handleSelectAll}
                    size="small"
                    sx={{ padding: '0px', '& svg': { fontSize: '20px' } }}
                  />
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canDelete ? 6 : 5} align="center" sx={{ py: 8 }}>
                  <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                    <Typography variant="h6" color="text.secondary">
                      {searchTerm ?
                        'Không tìm thấy danh mục nào phù hợp với điều kiện tìm kiếm' :
                        'Chưa có danh mục nào trong hệ thống'
                      }
                    </Typography>
                    {(searchTerm) && (
                      <Typography variant="body2" color="text.secondary">
                        Thử thay đổi từ khóa tìm kiếm
                      </Typography>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              categories.map((category) => {
                const bookCount = category.bookCount || 0;
                const isSelected = selectedCategoryIds.includes(category.id);

                logger.log('🔍 CategoryList - Rendering category:', {
                  id: category.id,
                  name: category.name,
                  bookCount,
                  isSelected,
                  canDelete
                });

                return (
                  <TableRow key={category.id}>
                    <TableCell>
                      <Link
                        component="button"
                        variant="subtitle2"
                        fontWeight="bold"
                        onClick={() => {
                          navigate('/books', {
                            state: {
                              fromCategories: true,
                              categoryId: category.id,
                              categoryName: category.name
                            }
                          });
                        }}
                        sx={{
                          cursor: 'pointer',
                          textDecoration: 'none',
                          color: 'primary.main',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        {category.name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {category.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="medium">
                        {category.bookCount || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(category.dateCreated)}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => onEdit(category)}
                        title="Chỉnh sửa"
                        disableFocusRipple
                        disableRipple
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(category)}
                        title="Xóa"
                        color="error"
                        disableFocusRipple
                        disableRipple
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                    {canDelete && (
                      <TableCell padding="none" align="center" sx={{ width: '14px', minWidth: '14px', maxWidth: '14px', padding: '0px !important' }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSelectCategory(category.id, bookCount);
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          size="small"
                          sx={{ padding: '0px', '& svg': { fontSize: '20px' } }}
                          title="Chọn để xóa"
                        />
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Xác nhận xóa</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Bạn có chắc chắn muốn xóa danh mục "{categoryToDelete?.name}" không?
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Lưu ý: Không thể xóa danh mục nếu có sách đang sử dụng danh mục này. Vui lòng chuyển các sách sang danh mục khác trước khi xóa.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Hủy</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Xóa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onClose={handleBulkDeleteCancel}>
        <DialogTitle>Xác nhận xóa nhiều danh mục</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Bạn có chắc chắn muốn xóa {selectedCategoryIds.length} danh mục không?
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Lưu ý: Chỉ có thể xóa các danh mục có số lượng sách = 0. Các danh mục có sách sẽ không được xóa.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkDeleteCancel}>Hủy</Button>
          <Button onClick={handleBulkDeleteConfirm} color="error" variant="contained">
            Xóa
          </Button>
        </DialogActions>
      </Dialog>

      <ToastNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />
    </Box>
  );
};
